/*
  NominateForReviewDialog — flag a happy client for a Google review
  request. Loads the active trigger list, calls POST /api/reviews/nominate,
  and surfaces the outcome (promoted / blocked reason).

  A nomination still runs the full eligibility gate server-side and
  still needs manager approval before dispatch — this button just
  saves the practitioner from digging through the Reviews page.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Star } from "lucide-react";

const BLOCK_MESSAGES = {
  do_not_contact: "This client has opted out of contact — no request will be created.",
  no_phone: "SMS trigger, but no phone number on file. Add a phone or pick an email trigger.",
  cooldown: "This client has been sent a review request within the trigger's cooldown window.",
  concurrent_active: "There's already an active review request in the queue for this client.",
  template_missing: "The trigger's template is missing. Fix it in Reviews → Triggers.",
  promoted: "",
  not_candidate: "",
};

export default function NominateForReviewDialog({
  open, onOpenChange, clientEmail, clientName,
}) {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await api.get("/reviews/triggers?include_inactive=false");
      const list = (r.data?.triggers || []).filter((t) => t.active);
      setTriggers(list);
      setSelected(list[0]?.id || "");
    } catch (e) {
      toast.error("Couldn't load triggers", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  async function nominate() {
    if (!selected) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/reviews/nominate", {
        client_email: clientEmail, trigger_id: selected,
      });
      setResult(r.data);
      const outcome = r.data?.outcome;
      if (outcome === "promoted") {
        toast.success("Draft created — awaiting manager approval");
      } else if (BLOCK_MESSAGES[outcome]) {
        toast.info(BLOCK_MESSAGES[outcome]);
      } else {
        toast.info(`Outcome: ${outcome}`);
      }
    } catch (e) {
      toast.error("Nomination failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="nominate-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" fill="currentColor" />
            Nominate for a Google review
          </DialogTitle>
          <DialogDescription>
            Flag <b>{clientName || clientEmail}</b> for a review request. A
            manager still needs to approve the draft before it sends.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-zinc-500 py-6 text-center">
            <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading triggers…
          </div>
        ) : triggers.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 border rounded p-3"
               data-testid="nominate-review-no-active">
            No active triggers. Ask your manager to activate one in
            Reviews → Triggers.
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-2xs uppercase text-zinc-500">Under trigger</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
                      className="w-full h-9 text-sm border rounded px-2 bg-white"
                      data-testid="nominate-review-trigger">
              {triggers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.channel})
                </option>
              ))}
            </select>
            {result && (
              <div className={`text-xs mt-2 p-2 rounded border ${
                result.outcome === "promoted"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}
                   data-testid="nominate-review-result">
                {result.outcome === "promoted"
                  ? "Draft created and queued for manager approval."
                  : (BLOCK_MESSAGES[result.outcome] || `Outcome: ${result.outcome}`)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={nominate} disabled={busy || !selected || triggers.length === 0}
                  data-testid="nominate-review-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Star className="h-3.5 w-3.5 mr-1" />}
            Nominate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
